import {
  LayoutDashboard,
  Building2,
  Target,
  Mail,
  ListTodo,
  MessageSquare,
  FileText,
  Hash,
  Stethoscope,
  BookOpen,
  Contact,
  Presentation,
  UserCog,
  Settings,
  CalendarDays,
} from "lucide-react";

export const TASK_PATHS = ["/tasks", "/mein-tag", "/meine-woche", "/planung"];

// Gruppierte Hauptnavigation. Bestehende Routen bleiben unverändert –
// nur Beschriftung/Struktur wurde vereinheitlicht (datensicher).
export const navSections = [
  {
    label: null,
    items: [
      { path: "/", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    label: "Vertrieb",
    items: [
      { path: "/customers", label: "Kunden", icon: Building2 },
      { path: "/praxissuche", label: "Leads", icon: Target },
      { path: "/mailing", label: "Rundmails", icon: Mail },
    ],
  },
  {
    label: "Produktivität",
    items: [
      { path: "/tasks", label: "Aufgaben", icon: ListTodo, match: (p) => TASK_PATHS.some((x) => p.startsWith(x)) },
      { path: "/kalender", label: "Kalender", icon: CalendarDays },
      { path: "/chat", label: "Team-Chat", icon: MessageSquare },
    ],
  },
  {
    label: "Service",
    items: [
      { path: "/service-reports", label: "Serviceberichte", icon: FileText },
      { path: "/serial-numbers", label: "Seriennummern", icon: Hash },
      { path: "/medical-software", label: "Arztsoftware", icon: Stethoscope },
    ],
  },
  {
    label: "Ressourcen",
    items: [
      { path: "/knowledge", label: "Wissensdatenbank", icon: BookOpen },
      { path: "/visitenkarten", label: "Visitenkarten", icon: Contact },
      { path: "/webinar-hintergruende", label: "Webinar-Hintergründe", icon: Presentation },
    ],
  },
  {
    label: "Administration",
    items: [
      { path: "/users", label: "Benutzer", icon: UserCog },
      { path: "/settings", label: "Einstellungen", icon: Settings },
    ],
  },
];

// Flache Listen für Rückwärtskompatibilität (CommandPalette, MobileBottomNav, Favoriten).
export const allNavItems = navSections.flatMap((s) => s.items);
export const navItems = allNavItems;
export const internItems = [];

// Titel-/Breadcrumb-Auflösung für die Kopfzeile.
const EXTRA_TITLES = {
  "/mein-tag": "Mein Tag",
  "/meine-woche": "Meine Woche",
  "/planung": "Aufgaben",
  "/service-reports/new": "Neuer Servicebericht",
  "/admin/errors": "Fehler-Protokoll",
  "/diagnose": "System-Diagnose",
};

export function resolvePageTitle(pathname) {
  if (EXTRA_TITLES[pathname]) return EXTRA_TITLES[pathname];
  // Detailseiten (/customers/:id, /service-reports/:id/edit)
  const exact = allNavItems.find((i) => i.path === pathname);
  if (exact) return exact.label;
  const startsWith = allNavItems
    .filter((i) => i.path !== "/" && pathname.startsWith(i.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (startsWith) return startsWith.label;
  return "Dashboard";
}

// Top-5 Schnellzugriff (Doctolib-Stil Kopfleiste).
export const quickAccess = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard, match: (p) => p === "/" },
  { path: "/customers", label: "Kunden", icon: Building2, match: (p) => p.startsWith("/customers") },
  { path: "/service-reports", label: "Serviceberichte", icon: FileText, match: (p) => p.startsWith("/service-reports") },
  { path: "/tasks", label: "Aufgaben", icon: ListTodo, match: (p) => TASK_PATHS.some((x) => p.startsWith(x)) },
  { path: "/kalender", label: "Kalender", icon: CalendarDays, match: (p) => p.startsWith("/kalender") },
];

// Tiefe Breadcrumb-Ebene je nach Pfadsuffix.
function deepLabel(pathname) {
  if (pathname.endsWith("/new")) return "Neu anlegen";
  if (pathname.endsWith("/edit")) return "Bearbeiten";
  if (pathname.startsWith("/customers/")) return "Kundenprofil";
  if (pathname.startsWith("/service-reports/")) return "Servicebericht";
  return "Details";
}

// Liefert Breadcrumb-Kette: [{label, to?}]. Letztes Element ohne `to` = aktuell.
export function resolveBreadcrumbs(pathname) {
  const crumbs = [{ label: "Start", to: "/" }];
  if (pathname === "/") return crumbs;
  if (EXTRA_TITLES[pathname]) {
    const seg = allNavItems
      .filter((i) => i.path !== "/" && pathname.startsWith(i.path))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (seg && seg.path !== pathname) crumbs.push({ label: seg.label, to: seg.path });
    crumbs.push({ label: EXTRA_TITLES[pathname] });
    return crumbs;
  }
  const seg = allNavItems
    .filter((i) => i.path !== "/" && pathname.startsWith(i.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (!seg) {
    crumbs.push({ label: resolvePageTitle(pathname) });
    return crumbs;
  }
  if (pathname === seg.path) {
    crumbs.push({ label: seg.label });
  } else {
    crumbs.push({ label: seg.label, to: seg.path });
    crumbs.push({ label: deepLabel(pathname) });
  }
  return crumbs;
}
